/**
 * PATH: apps/quote/src/lib/trade-mode.ts
 * FILE_ID: QB-LIB-TRADEMODE-001
 * PURPOSE: Single source of truth for the Quote Hub trade-mode contract
 *          (waterproofing | steel | custom). Mirrors
 *          `Organisation.QUOTE_BUILDER_MODE_CHOICES` on the Django side
 *          so the frontend, tests, and the column gate all agree on
 *          the same literal type.
 *
 * WHY: Bravada's Quote Hub previously had no trade switch. SAM-parity
 *      work bolted steel-fabrication allowance buckets onto every
 *      quote, including waterproofing tenants. This module names the
 *      trade-mode union so other modules (`buildAgGridColumns`,
 *      `useQuoteBuilderTradeMode`, the Setup tab selector) can
 *      consume one shared type rather than redefining string unions
 *      and drifting apart.
 *
 * FLOW:
 *   ┌──────────────────────┐    ┌─────────────────────────────────┐
 *   │ Organisation.        │ -> │ frontend reads via              │
 *   │ quote_builder_mode   │    │ /api/organisations/<id>/        │
 *   │ (Django, CharField)  │    │ settings/  →  QuoteBuilderTrade │
 *   └──────────────────────┘    └─────────────────────────────────┘
 *
 * RELATED:
 *   - scope-ag-grid-columns.ts — consumes `tradeMode` in BuildOptions
 *   - useQuoteBuilderTradeMode.ts — fetches + caches the per-org mode
 *   - QB-AGGRID-ALLOWANCES-001 — band gated by waterproofing mode
 */

/**
 * Trade-mode literal union. Keep in sync with
 * `organisation/models/core.py::Organisation.QUOTE_BUILDER_MODE_CHOICES`
 * (Django) and the choices in migration 0119.
 */
export const QUOTE_BUILDER_TRADE_MODES = [
  'waterproofing',
  'steel',
  'custom',
] as const

export type QuoteBuilderTradeMode = (typeof QUOTE_BUILDER_TRADE_MODES)[number]

/**
 * Friendly labels for the trade modes — shown in the Setup tab selector
 * and any future onboarding wizard. Mirrors the human-readable half of
 * `QUOTE_BUILDER_MODE_CHOICES` so backend and frontend agree on copy.
 */
export const TRADE_MODE_LABELS: Record<QuoteBuilderTradeMode, string> = {
  waterproofing: 'Waterproofing',
  steel: 'Steel Fabrication',
  custom: 'Custom (show all columns)',
}

/**
 * I→O type-guard. Narrows an arbitrary value (typically `unknown`
 * coming back from the JSON API) into the trade-mode union, or
 * returns null when the value is missing / malformed. Used at the
 * boundary so we never propagate untyped strings into UI code.
 */
export function parseQuoteBuilderTradeMode(
  value: unknown,
): QuoteBuilderTradeMode | null {
  if (typeof value !== 'string') return null
  return (QUOTE_BUILDER_TRADE_MODES as readonly string[]).includes(value)
    ? (value as QuoteBuilderTradeMode)
    : null
}

/**
 * Backend default — what every freshly-created Organisation gets when
 * no mode is explicitly set. Documented here so the frontend hook can
 * fall back to the same value during the initial load to avoid a
 * one-frame "show steel columns then hide" flicker for waterproofers
 * (the dominant tenant type).
 */
export const DEFAULT_TRADE_MODE: QuoteBuilderTradeMode = 'waterproofing'
